import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import GoogleMapReact from 'google-map-react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  COLOR_BLACK,
  COLOR_PRIMARY_DARK,
  COLOR_RED,
  mapOptions,
  staticMapOptions,
} from 'src/const';
import { GOOGLE_MAPS_API_KEY } from 'src/keys';
import { getPolygonPaths } from 'src/App/Pages/OverviewPage/helper';
import { applySmartBounds, setPolygon } from 'src/utils/functions';
import { COLOR_PRIMARY_2, MAP_FILL_COLOR } from 'src/colors';
import { Bounds } from 'src/interfaces/idashboard';
import EditButton from 'src/App/Components/Buttons/EditButton';
import FilterBody from 'src/App/HOC/Filter/FilterBody';
import { FilterVal } from 'src/interfaces/iproptypes';
import BedroomFilter from 'src/App/HOC/Filter/BedroomFilter';
import { useUpdateCustomMarketName } from 'src/hooks/useCustomHooks';

const FilterSelectionModal = ({
  handleClose,
  center,
  zoom,
  submarketPolygonPaths,
  setBoundarySelectionModalOpen,
  allListings,
  filterVal,
  setFilterVal,
  matches,
  currency,
  revPotentialRangeInvestment,
  setIndividualRevenuePotentialRange,
  handleOnSampleClick,
  priceTiers,
  onSubmit,
  submarketName,
  setSubmarketName,
  loading,
  setLoading,
  setSuccess,
  handleModalOnClose
}) => {
  const search = useSelector(state => (state as any).dashboard.search);

  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded ? true : false);
  };

  const {
    editableName,
    setEditableName,
    nameLoading,
    nameUpdateStatus,
    handleNameCheck,
    checkOrUpdateTailoredRegionName,
  } = useUpdateCustomMarketName(submarketName, setSubmarketName);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onSubmit();
    }, 10);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    setEditableName(submarketName);
  }, [submarketName]);

  const postOpsAfterSubmarketCreation = () => {
    setSuccess(true);
    const tailoredRegionModal = `${window.location.origin}/airbnb-data/0/0/${editableName}`;
    setTimeout(() => {
        window.open(tailoredRegionModal, '_blank');
        handleModalOnClose();
    }, 2000);
  };


  const callbackAfterMapIsLoaded = (
    map,
    maps,
    polygonRaw: string,
    multiPolygonRaw: string,
    submarketPolygonArray
  ) => {
    const paths = getPolygonPaths(polygonRaw, multiPolygonRaw);
    setPolygon(map, maps, paths, COLOR_PRIMARY_DARK, MAP_FILL_COLOR);

    const polygonCoordinates = submarketPolygonArray.map(coord => ({
      lat: coord[0],
      lng: coord[1],
    }));
    const submarketPolygon = setPolygon(map, maps, polygonCoordinates, COLOR_BLACK, MAP_FILL_COLOR);
    applySmartBounds(map, maps, {} as Bounds, [submarketPolygon]);
  };

  return (
    <Grid
      container
      direction="column"
      sx={{
        width: '100%',
        height: 'auto',
      }}
      gap={2}
    >
      <Grid container item direction="row" justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="body1">Create Submarket in {search.data.name}</Typography>
        </Grid>
        <Grid item>
          <IconButton>
            <CloseIcon
              style={{ color: COLOR_RED, fontSize: 40, marginRight: 0 }}
              onClick={handleClose}
            />
          </IconButton>
        </Grid>
      </Grid>

      <Grid container item direction="column" spacing={2} gap={2} sx={{ height: 'auto' }}>
        {/* Location Snapshot */}
        <Grid item>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
            Location
          </Typography>
          <Grid
            sx={{
              height: '400px',
              width: '100%',
              background: '#FFF',
              boxShadow: '0px 4px 40px -20px rgba(0, 0, 0, 0.25)',
              borderRadius: '20px',
              padding: '20px',
              paddingTop: '60px',
              position: 'relative',
              top: 0,
              left: 0,
            }}
          >
            <Box sx={{ position: 'absolute', top: '10px', right: '10px' }}>
              <EditButton
                isDisabled={false}
                actionOnClick={() => {
                  setBoundarySelectionModalOpen(true);
                }}
                isLocked={false}
              />
            </Box>
            <GoogleMapReact
              bootstrapURLKeys={{
                key: GOOGLE_MAPS_API_KEY,
                libraries: ['drawing', 'places', 'visualization'],
                id: 'filter-selection-modal-map',
              }}
              center={center}
              options={{
                ...mapOptions,
                ...staticMapOptions,
              }}
              zoom={zoom}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={({ map, maps }) => {
                callbackAfterMapIsLoaded(
                  map,
                  maps,
                  search.data.polygon,
                  search.data.multiPolygon,
                  submarketPolygonPaths
                );
              }}
            />
          </Grid>
        </Grid>

        {/* Submarket Name */}
        <Grid
          item
          sx={{
            background: '#FFF',
            boxShadow: '0px 4px 40px -20px rgba(0, 0, 0, 0.25)',
            borderRadius: '20px',
            padding: '20px',
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
            Submarket Name
          </Typography>

          <TextField
            variant="standard"
            fullWidth
            value={editableName}
            disabled={loading}
            onChange={handleNameCheck}
            InputProps={{
              endAdornment: (loading || nameLoading) && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              )
            }}
            error={nameUpdateStatus.error}
            helperText={
              nameLoading ? <CircularProgress size={20} /> : nameUpdateStatus.message
            }
            FormHelperTextProps={{
              style: { color: nameUpdateStatus.error ? 'red' : 'green' },
            }}
          />
        </Grid>

      </Grid>

      <Grid container item direction="row" justifyContent="flex-end">
        <Button
          size="large"
          variant="outlined"
          sx={{
            color: 'white',
            background: COLOR_PRIMARY_2,
            ':hover': { color: 'white', background: COLOR_PRIMARY_2 },
          }}
          onClick={async () => {
            setLoading(true);
            await checkOrUpdateTailoredRegionName('rename', editableName);
            setLoading(false);
            postOpsAfterSubmarketCreation();
          }}
          disabled={loading}
        >
          Create Submarket {loading && <CircularProgress />}
        </Button>
      </Grid>
    </Grid>
  );
};

export default FilterSelectionModal;
